import dayjs from "dayjs";
import "dayjs/locale/es";
import { hoyChile } from "./fechaChile";

/**
 * Formatea una fecha para presentación al usuario.
 *
 * Formatos soportados:
 *   "DD-MM-YYYY"  → 25-03-2026
 *   "DD-MM"       → 25-03
 *   "MM-YYYY"     → 03-2026
 *   "DD-MM-YYYY HH:mm" → 25-03-2026 14:30
 *
 * Acepta string (ISO, YYYY-MM-DD, etc.), Date, o dayjs.
 * Retorna "—" si el valor es nulo/inválido.
 */
export function formatDate(
  value: string | Date | dayjs.Dayjs | null | undefined,
  format: "DD-MM-YYYY" | "DD-MM" | "MM-YYYY" | "DD-MM-YYYY HH:mm" = "DD-MM-YYYY",
): string {
  if (value == null) return "—";
  const d = dayjs(value);
  if (!d.isValid()) return "—";
  return d.format(format);
}

const DIA_CALENDARIO = /^(\d{4})-(\d{2})-(\d{2})/;

interface OpcionesFechaConDia {
  /** `false` para usarla dentro de una frase («Aseos del lunes, septiembre 14»). */
  mayuscula?: boolean;
  /** Año que se considera actual. Por defecto, el de hoy en Chile. */
  anioActual?: number;
}

/**
 * Un día calendario con su día de la semana, en español, separado del año:
 * `{ dia: "Lunes, septiembre 14", anio: null }` si es del año actual en Chile, y
 * `{ dia: "Domingo, septiembre 14", anio: 2025 }` si no. `null` si el valor es
 * nulo o no es un día que exista.
 *
 * Separar el año deja que una tabla lo ponga en otra línea, más chico, sin partir
 * el día de la semana de su número.
 *
 * Un string `YYYY-MM-DD` —o un ISO que empieza así— se toma como día calendario
 * y nunca se convierte desde UTC: las fechas de aseos son DATEONLY, y leer
 * `"2026-09-14T00:00:00.000Z"` como instante lo corre al domingo 13 en Chile.
 * El español se pide en cada llamada, así que no depende de que otra página haya
 * cambiado el idioma global de dayjs.
 */
export function partesFechaConDia(
  value: string | Date | dayjs.Dayjs | null | undefined,
  { mayuscula = true, anioActual = Number(hoyChile().slice(0, 4)) }: OpcionesFechaConDia = {},
): { dia: string; anio: number | null } | null {
  if (value == null || value === "") return null;
  let d: dayjs.Dayjs;
  if (typeof value === "string") {
    const partes = DIA_CALENDARIO.exec(value);
    if (!partes) return null;
    const dia = `${partes[1]}-${partes[2]}-${partes[3]}`;
    d = dayjs(dia);
    // dayjs corre los días que no existen (2026-02-30 → 2026-03-02): si la
    // vuelta no da el mismo texto, no es un día válido.
    if (!d.isValid() || d.format("YYYY-MM-DD") !== dia) return null;
  } else {
    d = dayjs(value);
    if (!d.isValid()) return null;
  }
  const texto = d.locale("es").format("dddd, MMMM D");
  return {
    dia: mayuscula ? texto.charAt(0).toUpperCase() + texto.slice(1) : texto,
    anio: d.year() === anioActual ? null : d.year(),
  };
}

/**
 * Lo mismo en una sola línea: «Lunes, septiembre 14», o «Domingo, septiembre 14,
 * 2025» si no es del año actual en Chile. Retorna "—" si no es un día válido.
 */
export function formatFechaConDia(
  value: string | Date | dayjs.Dayjs | null | undefined,
  opciones: OpcionesFechaConDia = {},
): string {
  const partes = partesFechaConDia(value, opciones);
  if (!partes) return "—";
  return partes.anio == null ? partes.dia : `${partes.dia}, ${partes.anio}`;
}
